import React from "react";
import LegalPage, { LegalSection } from "@/components/templates/LegalPage";
import { COMPANY, addressInline } from "@/data/company";

const EFFECTIVE = "February 2026";

const PrivacyPolicy = () => (
  <LegalPage
    testid="privacy-policy-page"
    title="Privacy Policy"
    description="How Lutrom Engineering Ltd collects, uses and protects personal information. UK GDPR-aligned privacy policy for our website and business relationships."
    path="/privacy-policy"
    h1="Privacy Policy"
    effective={EFFECTIVE}
  >
    <p>
      This Privacy Policy explains how {COMPANY.name} (&ldquo;we&rdquo;, &ldquo;us&rdquo; or
      &ldquo;our&rdquo;) collects, uses and protects personal information you provide to us
      through our website (<a href="https://www.lutrom.co.uk">www.lutrom.co.uk</a>), by phone,
      by email or in the course of a business relationship. It is intended to be read alongside
      our <a href="/cookie-policy">Cookie Policy</a> and
      {" "}<a href="/terms-and-conditions">Terms &amp; Conditions</a>.
    </p>

    <LegalSection title="Who we are">
      <p>
        {COMPANY.name} ({COMPANY.registered} · Company No. {COMPANY.companyNumber}) is the data
        controller for personal information collected through our website and in the course of
        our engineering services in the United Kingdom.
      </p>
      <p>
        Registered office and correspondence address:{" "}
        <span className="text-white">{addressInline()}</span>.
      </p>
      <p>
        For any privacy-related enquiry please contact us at{" "}
        <a href={COMPANY.email.mailto}>{COMPANY.email.display}</a> or by post to the address
        above.
      </p>
    </LegalSection>

    <LegalSection title="What information we collect">
      <p>We typically collect the following categories of personal information:</p>
      <ul className="list-disc pl-6 space-y-1.5">
        <li>
          <strong className="text-white">Contact information</strong> you provide via our
          enquiry form, by email or by phone — name, business name (where relevant), email
          address, phone number and the content of your enquiry.
        </li>
        <li>
          <strong className="text-white">Site and technical information</strong> collected
          automatically when you visit the website — including your IP address, browser type,
          approximate location (derived from your IP), pages visited and referrer. See our
          {" "}<a href="/cookie-policy">Cookie Policy</a> for more detail.
        </li>
        <li>
          <strong className="text-white">Business-relationship information</strong> collected
          during quoting, invoicing, service delivery and support — including site addresses,
          asset registers, engineering records and correspondence with your team.
        </li>
      </ul>
      <p>
        We do not knowingly collect any special-category personal data (such as health or
        biometric data) through the website. Please do not send us special-category information
        via our enquiry form.
      </p>
    </LegalSection>

    <LegalSection title="How we use your information (our lawful bases)">
      <p>Under UK GDPR we rely on the following lawful bases:</p>
      <ul className="list-disc pl-6 space-y-1.5">
        <li>
          <strong className="text-white">Contract or steps prior to entering a contract</strong>{" "}
          — to respond to enquiries, produce quotes, deliver services, invoice and manage the
          working relationship.
        </li>
        <li>
          <strong className="text-white">Legitimate interests</strong> — to run and improve our
          website, maintain security, communicate with existing customers about our services,
          and keep basic records of correspondence.
        </li>
        <li>
          <strong className="text-white">Consent</strong> — for optional cookies and analytics
          where required (see our <a href="/cookie-policy">Cookie Policy</a>). You can withdraw
          consent at any time.
        </li>
        <li>
          <strong className="text-white">Legal obligation</strong> — where we are required by
          law to retain records (for example, statutory financial records).
        </li>
      </ul>
    </LegalSection>

    <LegalSection title="Who we share your information with">
      <p>
        We do not sell personal information. We share information only with parties who help us
        run our business, and only to the extent necessary. Typical categories include:
      </p>
      <ul className="list-disc pl-6 space-y-1.5">
        <li>Our website hosting, email and cloud-storage providers.</li>
        <li>
          Our analytics and messaging providers used on the website — currently Google
          Analytics 4 and Crisp Chat (see our <a href="/cookie-policy">Cookie Policy</a>).
        </li>
        <li>Our accountants and professional advisers.</li>
        <li>
          Sub-contractors and specialist partners engaged as part of delivering a specific
          service, where their involvement is necessary for the work.
        </li>
        <li>
          Regulators, courts and law-enforcement bodies where required by law or in support of
          legal claims.
        </li>
      </ul>
      <p>
        Where a provider processes personal data outside the United Kingdom or the European
        Economic Area, we take reasonable steps to ensure that transfer safeguards required by
        UK GDPR are in place (for example, Standard Contractual Clauses or an adequacy
        decision).
      </p>
    </LegalSection>

    <LegalSection title="How long we keep your information">
      <p>
        We keep personal information only for as long as is reasonably necessary for the
        purpose we collected it for, and to meet legal, accounting and reporting requirements.
        Enquiries that do not result in a working relationship are typically deleted within a
        reasonable period after last contact. Records related to delivered work are retained
        for as long as we are legally required to keep them (typically at least six years for
        accounting purposes).
      </p>
    </LegalSection>

    <LegalSection title="Your rights">
      <p>Under UK GDPR you have the following rights in respect of your personal information:</p>
      <ul className="list-disc pl-6 space-y-1.5">
        <li>The right to be informed about how we use your information.</li>
        <li>The right of access to a copy of the information we hold about you.</li>
        <li>The right to rectification of inaccurate information.</li>
        <li>
          The right to erasure (also known as the &lsquo;right to be forgotten&rsquo;) in
          certain circumstances.
        </li>
        <li>The right to restrict processing in certain circumstances.</li>
        <li>The right to data portability in certain circumstances.</li>
        <li>
          The right to object to processing based on legitimate interests, and to direct
          marketing at any time.
        </li>
        <li>The right to withdraw consent, where consent is the lawful basis.</li>
      </ul>
      <p>
        To exercise any of these rights please email us at{" "}
        <a href={COMPANY.email.mailto}>{COMPANY.email.display}</a>. We will respond within the
        time frame required by law.
      </p>
      <p>
        If you are not satisfied with how we have handled your personal information you have
        the right to complain to the Information Commissioner&apos;s Office (ICO) — see{" "}
        <a href="https://ico.org.uk" rel="noopener noreferrer" target="_blank">
          ico.org.uk
        </a>
        . We would appreciate the opportunity to address your concerns directly first.
      </p>
    </LegalSection>

    <LegalSection title="Security">
      <p>
        We take reasonable technical and organisational measures to protect the personal
        information we hold, including access controls, encrypted transmission of website
        traffic (HTTPS) and staff awareness. No system is completely secure, and we cannot
        guarantee the security of information transmitted over the internet.
      </p>
    </LegalSection>

    <LegalSection title="Changes to this policy">
      <p>
        We may update this Privacy Policy from time to time to reflect changes to our services,
        the law or our operations. The &ldquo;Last updated&rdquo; date at the top of this page
        indicates when the policy was last revised.
      </p>
    </LegalSection>

    <LegalSection title="Contact">
      <p>
        For any question about this Privacy Policy or how we handle personal information please
        contact:
      </p>
      <p className="text-white">
        {COMPANY.name}
        <br />
        {addressInline()}
        <br />
        <a href={COMPANY.email.mailto}>{COMPANY.email.display}</a>
        <br />
        <a href={COMPANY.phone.tel}>{COMPANY.phone.display}</a>
      </p>
    </LegalSection>
  </LegalPage>
);

export default PrivacyPolicy;
